import type { TutorTheme } from '../contexts/tutorThemeContext.shared';

/** Tamil platform defaults — valid only after explicit fallback resolution, not while pending. */
export const TAMIL_TUTOR_THEME_FALLBACK: TutorTheme = {
  teacherImage: '/images/tamil-teacher.png',
  backgroundImage: '/images/tutor-classroom-bg.webp',
};

export const MALAY_DIALECT_TUTOR_THEME_FALLBACK: TutorTheme = {
  teacherImage: '/images/malay-teacher.png',
  backgroundImage: '/images/malay-classroom-bg.png',
};

export function isMalayDialectKey(dialectKey: string): boolean {
  const dialect = dialectKey.trim().toLowerCase();
  return (
    dialect.startsWith('malay') ||
    dialect === 'ms' ||
    dialect === 'ms-sg' ||
    dialect === 'bahasa_melayu'
  );
}

export function dialectTutorThemeFallback(dialectKey: string): TutorTheme {
  return isMalayDialectKey(dialectKey) ? MALAY_DIALECT_TUTOR_THEME_FALLBACK : TAMIL_TUTOR_THEME_FALLBACK;
}

/**
 * Stable visual identity for signed URLs — origin + pathname, ignoring query signatures.
 * Local/static paths compare literally.
 */
export function stableThemeAssetIdentity(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const parsed = new URL(trimmed);
      return `${parsed.origin}${parsed.pathname}`;
    }
  } catch {
    // fall through
  }
  return trimmed.split('?')[0] ?? trimmed;
}

/** Same physical asset — signature/query may differ. */
export function isSameThemeAsset(a: string, b: string): boolean {
  const idA = stableThemeAssetIdentity(a);
  const idB = stableThemeAssetIdentity(b);
  return Boolean(idA && idB && idA === idB);
}

/**
 * Merge incoming theme URLs with prior cache/display values.
 * Refreshes signed URL when identity matches; replaces when path differs.
 */
export function mergeThemeAssetUrls(previous: TutorTheme, incoming: TutorTheme): TutorTheme {
  return {
    teacherImage: isSameThemeAsset(previous.teacherImage, incoming.teacherImage)
      ? incoming.teacherImage
      : incoming.teacherImage,
    backgroundImage: isSameThemeAsset(previous.backgroundImage, incoming.backgroundImage)
      ? incoming.backgroundImage
      : incoming.backgroundImage,
  };
}

function preloadImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    if (!url.trim()) {
      resolve();
      return;
    }
    const img = new Image();
    img.decoding = 'async';
    const finish = () => resolve();
    img.onload = finish;
    img.onerror = finish;
    img.src = url;
    if (typeof img.decode === 'function') {
      void img.decode().then(finish).catch(finish);
    }
  });
}

/** Preload teacher + background; resolves even on error (caller decides fallback). */
export async function preloadTutorThemeImages(
  theme: TutorTheme,
  isCancelled?: () => boolean,
): Promise<void> {
  if (isCancelled?.()) return;
  await Promise.all([preloadImage(theme.teacherImage), preloadImage(theme.backgroundImage)]);
}
